import { defaultLocale } from './default-locale';

/**
 * @name locales
 * @description Supported locales
 *
 * Listed explicitly rather than derived from `defaultLocale`. The original
 * `[defaultLocale, ...]` made the set a function of the default, so setting
 * `NEXT_PUBLIC_DEFAULT_LOCALE=ar` did not *add* Arabic — it replaced English,
 * and every English message became a missing key.
 *
 * The default is still env-driven, so it is folded in below rather than
 * assumed: an operator who points the default at a locale that is not listed
 * gets that locale supported instead of a router that 404s its own home page.
 */
const supported = [
  'en', // English
  'ar', // Arabic — right-to-left, see `isRtl` below
];

export const locales: string[] = supported.includes(defaultLocale)
  ? supported
  : [defaultLocale, ...supported];

/**
 * Locales written right to left.
 *
 * Drives the `dir` attribute on `<html>`. A set rather than a check on the
 * locale string, because the answer is a property of the language and not
 * something derivable from its code.
 */
const RTL_LOCALES = new Set(['ar', 'he', 'fa', 'ur']);

export function isRtl(locale: string) {
  return RTL_LOCALES.has(locale);
}

/**
 * What each locale calls itself.
 *
 * Endonyms, not English names: someone looking for Arabic is looking for
 * "العربية", and a list that reads "Arabic" is a list written for people who
 * already read English.
 */
export const localeNames: Record<string, string> = {
  en: 'English',
  ar: 'العربية',
};
